// ═══════════════════════════════════════════════════════════════════════════════
// AI Settings — platform-wide AI-provider configuration
//
// Super-admin-only. NOT exposed to tenant admins — these settings affect
// every tenant in the platform.
//
// Layout (top → bottom):
//   1. Provider toggle (OpenAI vs Anthropic)
//   2. Side-by-side provider settings cards (OpenAI | Anthropic)
//   3. Generation strategy (parallel mode + brevity)
//   4. Secrets reference (what to set on the App edge function and when)
//
// All settings persist to platform_settings (Platform DB). The edge function
// generate-itinerary reads them on each invocation (60s cache). Changes take
// effect within ~60 seconds.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save, Check, Zap, Sparkles, AlertTriangle, Info, Thermometer, Cpu, Layers, Wand2, KeyRound, ShieldAlert, RefreshCw, X, BookOpen, MessageCircle, Globe2, FileText, Clock } from 'lucide-react';
import { cn } from '@/utils/helpers';
import {
  fetchPlatformAISettings,
  updatePlatformAISettings,
  fetchAppSecretStatus,
  unsetAppSecret,
  ITINERARY_OPENAI_MODEL_OPTIONS,
  ITINERARY_ANTHROPIC_MODEL_OPTIONS,
  ITINERARY_PARALLEL_MODE_OPTIONS,
  type ItineraryAIProvider,
  type ItineraryParallelMode,
  type PosterImageProvider,
  type PlatformAISettings,
  type ManageableAppSecret,
  type AppSecretStatus,
} from '@/lib/api';

// ─── Provider card metadata ──────────────────────────────────────────

type ProviderCard = {
  id:    ItineraryAIProvider;
  label: string;
  blurb: string;
  icon:  React.ComponentType<{ className?: string }>;
};

const PROVIDERS: ProviderCard[] = [
  { id: 'openai',    label: 'OpenAI',           blurb: 'Default provider. Stable, well-known.',                 icon: Zap      },
  { id: 'anthropic', label: 'Anthropic Claude', blurb: 'Stronger long-form structure. Verbose by default.',     icon: Sparkles },
];

// ═══════════════════════════════════════════════════════════════════════════════

export function AISettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<PlatformAISettings | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<PlatformAISettings>>({});

  // ── Live status of the App-project edge-function override secrets ──
  // Polled from the Platform manage-app-secrets edge function which in
  // turn queries the Supabase Management API. `null` while loading;
  // `configured=false` when the Platform PAT secret isn't set (UI
  // shows a "Not configured" hint in that case).
  const [secretStatus, setSecretStatus] = useState<{
    configured: boolean;
    secrets:    AppSecretStatus[];
    error?:     string;
  } | null>(null);
  const [refreshingSecrets, setRefreshingSecrets] = useState(false);
  const [unsetting,         setUnsetting]         = useState<string | null>(null);
  // Secrets Reference is now an icon → modal pattern, not an inline
  // section. Surfaces a small amber dot on the icon when any override
  // is currently set, so super-admins can see at a glance that the UI
  // is being overridden. The icon click navigates to the dedicated
  // /admin/secrets-reference page (formerly a modal — it outgrew 92vh
  // of dialog height when the catalog expanded).
  // Function Usage reference modal — opens from the second header
  // icon. Pure static documentation: which AI function uses which
  // provider/model, and when each fires. Updated when new AI
  // functions ship (hardcoded list in FunctionsModal below).
  const [functionsModalOpen, setFunctionsModalOpen] = useState(false);
  const hasActiveOverride = useMemo(
    () => !!secretStatus?.secrets.some(s => s.set),
    [secretStatus],
  );

  const loadSecretStatus = useCallback(async () => {
    setRefreshingSecrets(true);
    try {
      const s = await fetchAppSecretStatus();
      setSecretStatus(s);
    } catch (e) {
      setSecretStatus({ configured: false, secrets: [], error: (e as Error).message });
    } finally {
      setRefreshingSecrets(false);
    }
  }, []);

  const handleUnsetSecret = useCallback(async (name: ManageableAppSecret) => {
    setUnsetting(name);
    try {
      const r = await unsetAppSecret(name);
      if (!r.ok) {
        setError(r.error ?? `Failed to unset ${name}`);
      } else {
        // Refresh both — the secret status (now shows ✓ Unset) AND
        // the platform_settings row (now has fresh unset_at + unset_by).
        await Promise.all([loadSecretStatus(), load()]);
      }
    } finally {
      setUnsetting(null);
    }
  // load is defined further down; we use a callback ref pattern via
  // the closure below. Re-binding handled by the useEffect on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadSecretStatus]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlatformAISettings();
      setSettings(data);
      setDraft(data ?? {});
    } catch (err) {
      setError((err as Error).message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSecretStatus(); }, [loadSecretStatus]);

  const patch = useCallback(<K extends keyof PlatformAISettings>(
    key: K,
    value: PlatformAISettings[K],
  ) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  }, []);

  const dirtyFields = useMemo(() => {
    if (!settings) return [] as Array<keyof PlatformAISettings>;
    // Derive dirty keys from the draft itself rather than a hardcoded
    // whitelist. The whitelist pattern was fragile: every time a new
    // editable field shipped (poster_image_provider, marketplace tier
    // ladder, custom-price bounds, etc.) it had to be appended here or
    // the Save button stayed greyed-out even after the user edited the
    // input. A stale browser bundle running the OLD whitelist against
    // the NEW UI inputs caused silent Save no-ops — visible bug.
    //
    // Iterating draft keys is safe because:
    //   • `id`, `created_at`, `updated_at` are read-only — explicitly skipped.
    //   • Every other key in draft was put there either by the initial
    //     fetchPlatformAISettings load (= matches settings, so not dirty)
    //     or by a deliberate patch() call from a UI control.
    //   • The PostgREST UPDATE in updatePlatformAISettings stamps
    //     updated_at server-side, so passing through stale values is harmless.
    const SKIP: ReadonlySet<string> = new Set(['id', 'created_at', 'updated_at']);
    const out: Array<keyof PlatformAISettings> = [];
    for (const k of Object.keys(draft) as Array<keyof PlatformAISettings>) {
      if (SKIP.has(k as string)) continue;
      const dv = draft[k];
      if (dv === undefined) continue;
      if ((settings as any)[k] !== dv) out.push(k);
    }
    return out;
  }, [draft, settings]);

  const dirty = dirtyFields.length > 0;

  const handleSave = useCallback(async () => {
    if (!dirty) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updates: Partial<PlatformAISettings> = {};
      for (const k of dirtyFields) {
        (updates as any)[k] = draft[k];
      }
      const updated = await updatePlatformAISettings(updates);
      setSettings(updated);
      setDraft(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }, [dirty, dirtyFields, draft]);

  if (loading) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-surface-900">AI Settings</h1>
        </header>
        <div className="py-12 text-center text-surface-500">
          <Loader2 size={20} className="mx-auto animate-spin" />
          <p className="mt-2 text-sm">Loading current settings…</p>
        </div>
      </div>
    );
  }

  if (!settings) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold text-surface-900">AI Settings</h1>
        </header>
        <div className="rounded-2xl bg-red-50 border border-red-200 text-red-900 p-4 text-sm">
          <p className="font-semibold">Could not load settings.</p>
          <p className="mt-1 text-red-700">{error || 'Platform settings row not found. Run the migration first.'}</p>
        </div>
      </div>
    );
  }

  // Convenience reads — fall through draft → settings so the UI
  // always reflects what's about to be saved.
  const provider          = (draft.itinerary_ai_provider             ?? settings.itinerary_ai_provider) as ItineraryAIProvider;
  const openaiModel       = (draft.itinerary_openai_model            ?? settings.itinerary_openai_model);
  const anthropicModel    = (draft.itinerary_anthropic_model         ?? settings.itinerary_anthropic_model);
  const openaiTemp        = (draft.itinerary_temperature_openai      ?? settings.itinerary_temperature_openai);
  const anthropicTemp     = (draft.itinerary_temperature_anthropic   ?? settings.itinerary_temperature_anthropic);
  const anthropicMaxTok   = (draft.itinerary_anthropic_max_tokens    ?? settings.itinerary_anthropic_max_tokens);
  const parallelMode      = (draft.itinerary_parallel_mode           ?? settings.itinerary_parallel_mode) as ItineraryParallelMode;
  const brevityMode       = (draft.itinerary_brevity_mode            ?? settings.itinerary_brevity_mode);

  // Enrichment-side reads — independent provider/model/temperature
  // from itinerary so super-admin can use a fast/cheap LLM here.
  const enrichProvider       = (draft.enrichment_ai_provider          ?? settings.enrichment_ai_provider) as ItineraryAIProvider;
  const enrichOpenaiModel    = (draft.enrichment_openai_model         ?? settings.enrichment_openai_model);
  const enrichAnthropicModel = (draft.enrichment_anthropic_model      ?? settings.enrichment_anthropic_model);
  const enrichOpenaiTemp     = (draft.enrichment_temperature_openai     ?? settings.enrichment_temperature_openai);
  const enrichAnthropicTemp  = (draft.enrichment_temperature_anthropic  ?? settings.enrichment_temperature_anthropic);

  // Blitz-side reads — independent provider/model/temperature so
  // the conversational refine flow can run on a faster model than
  // the full itinerary generator (Blitz edits one day at a time,
  // doesn't need 30K-token outputs).
  const blitzProvider        = (draft.blitz_ai_provider               ?? settings.blitz_ai_provider) as ItineraryAIProvider;
  const blitzOpenaiModel     = (draft.blitz_openai_model              ?? settings.blitz_openai_model);
  const blitzAnthropicModel  = (draft.blitz_anthropic_model           ?? settings.blitz_anthropic_model);
  const blitzOpenaiTemp      = (draft.blitz_temperature_openai          ?? settings.blitz_temperature_openai);
  const blitzAnthropicTemp   = (draft.blitz_temperature_anthropic       ?? settings.blitz_temperature_anthropic);

  // Insider-side reads — independent so Insider tab generation can
  // be tuned for sub-30s wall clock (smaller model, lower max_tokens)
  // independent of itinerary build.
  const insiderProvider          = (draft.insider_ai_provider           ?? settings.insider_ai_provider) as ItineraryAIProvider;
  const insiderOpenaiModel       = (draft.insider_openai_model          ?? settings.insider_openai_model);
  const insiderAnthropicModel    = (draft.insider_anthropic_model       ?? settings.insider_anthropic_model);
  const insiderOpenaiTemp        = (draft.insider_temperature_openai     ?? settings.insider_temperature_openai);
  const insiderAnthropicTemp     = (draft.insider_temperature_anthropic  ?? settings.insider_temperature_anthropic);
  const insiderAnthropicMaxTok   = (draft.insider_anthropic_max_tokens   ?? settings.insider_anthropic_max_tokens);

  // Requirements-side reads — factual checklist content (lower temp).
  const reqProvider              = (draft.requirements_ai_provider           ?? settings.requirements_ai_provider) as ItineraryAIProvider;
  const reqOpenaiModel           = (draft.requirements_openai_model          ?? settings.requirements_openai_model);
  const reqAnthropicModel        = (draft.requirements_anthropic_model       ?? settings.requirements_anthropic_model);
  const reqOpenaiTemp            = (draft.requirements_temperature_openai     ?? settings.requirements_temperature_openai);
  const reqAnthropicTemp         = (draft.requirements_temperature_anthropic  ?? settings.requirements_temperature_anthropic);
  const reqAnthropicMaxTok       = (draft.requirements_anthropic_max_tokens   ?? settings.requirements_anthropic_max_tokens);

  // Journal Companion reads — independent provider/model/temp/max-tokens
  // for the daily-journal + trip-journal edge functions. Higher max
  // because trip recaps regularly run 4-8 paragraphs plus per-day
  // highlight blocks. Default provider is anthropic — prose narration
  // benefits from Claude's tone control, but operator can flip if they
  // prefer gpt-4o.
  const journalProvider          = (draft.journal_ai_provider                 ?? settings.journal_ai_provider) as ItineraryAIProvider;
  const journalOpenaiModel       = (draft.journal_openai_model                ?? settings.journal_openai_model);
  const journalAnthropicModel    = (draft.journal_anthropic_model             ?? settings.journal_anthropic_model);
  const journalOpenaiTemp        = (draft.journal_temperature_openai          ?? settings.journal_temperature_openai);
  const journalAnthropicTemp     = (draft.journal_temperature_anthropic       ?? settings.journal_temperature_anthropic);
  const journalAnthropicMaxTok   = (draft.journal_anthropic_max_tokens        ?? settings.journal_anthropic_max_tokens);

  // Trip Poster — image-generation provider + per-provider model
  // string. Model field is free-text so super-admin can flip to
  // gpt-image-2 / FLUX / Recraft / etc. the moment a provider ships
  // it, no redeploy needed. Only the active provider's input renders.
  const posterImageProvider = (draft.poster_image_provider ?? settings.poster_image_provider) as PosterImageProvider;
  const posterOpenaiModel   = (draft.poster_openai_model ?? settings.poster_openai_model);
  const posterGoogleModel   = (draft.poster_google_model ?? settings.poster_google_model);
  const posterFalModel      = (draft.poster_fal_model    ?? settings.poster_fal_model);
  // Trip Poster Marketplace — paid-clone economics. The tier array
  // is jsonb on the DB; supabase-js gives it back as number[]. The
  // share is numeric(4,3); supabase-js gives it back as number too.
  const posterPaidEnabled   = (draft.poster_paid_clones_enabled ?? settings.poster_paid_clones_enabled) as boolean;
  const posterPriceTiers    = (draft.poster_price_tiers_cents   ?? settings.poster_price_tiers_cents) as number[];
  const posterAuthorShare   = Number(draft.poster_author_revenue_share ?? settings.poster_author_revenue_share);
  // Custom-price input bounds — drive the "Custom" chip on
  // PosterGeneratePage. UX-only (no server enforcement on publish).
  const posterMinCustom     = Number(draft.poster_min_custom_cents ?? settings.poster_min_custom_cents ?? 100);
  const posterMaxCustom     = Number(draft.poster_max_custom_cents ?? settings.poster_max_custom_cents ?? 9900);

  return (
    <div className="space-y-6 pb-12">
      {/* Header — icon button on the right opens the Secrets
          Reference modal. Small amber dot on the icon when any
          override secret is currently set so the super-admin sees
          at a glance that the UI values are being overridden. */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-surface-900">AI Settings</h1>
          <p className="text-surface-500 mt-1">
            Platform-wide AI provider + tuning for trip-itinerary generation. Applies to every tenant — super-admin only.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Function Usage reference — opens a modal listing every
              AI-using edge function, its provider/model, and when it
              fires. Useful for super-admins planning capacity or
              debugging which function is doing what. */}
          <button
            type="button"
            onClick={() => setFunctionsModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-900 text-sm font-medium transition-colors"
            title="View AI Function Usage"
          >
            <BookOpen size={16} />
            <span>Functions</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/admin/secrets-reference')}
            className="relative inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-100 hover:bg-surface-200 text-surface-900 text-sm font-medium transition-colors"
            title="Open Secrets Reference"
          >
            <KeyRound size={16} />
            <span>Secrets</span>
            {hasActiveOverride && (
              <>
                {/* Pulsing amber dot — UI is currently being overridden
                    by at least one env var. Click to open the modal and
                    see which one. */}
                <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-amber-500 ring-2 ring-surface-0 animate-pulse" aria-hidden />
                <span className="sr-only">An override is currently active</span>
              </>
            )}
          </button>
        </div>
      </header>

      {/* Override note banner — env vars on the App edge function take
          precedence over these UI values. Concise here, full docs in
          the Secrets modal. */}
      <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200">
        <Info size={16} className="text-blue-700 mt-0.5 shrink-0" />
        <div className="text-sm text-blue-900 leading-snug">
          <p className="font-semibold">UI vs. environment variables</p>
          <p className="mt-0.5">
            Values you set here are read by the <code className="px-1 py-0.5 rounded bg-blue-100 font-mono text-xs text-blue-900">generate-itinerary</code> edge function on every call (60s cache).{' '}
            If the App-project edge-function has any of the override secrets set, the secret wins over this UI.{' '}
            <button
              type="button"
              onClick={() => navigate('/admin/secrets-reference')}
              className="underline font-medium hover:text-blue-700 transition-colors"
            >
              Open Secrets Reference →
            </button>
          </p>
        </div>
      </div>

      {/* ── Section 1: Active provider ──────────────────────────── */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader icon={Cpu} title="Active provider" subtitle="Which LLM runs the generate-itinerary edge function." />

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          {PROVIDERS.map(p => {
            const isSelected = provider === p.id;
            const Icon = p.icon;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => patch('itinerary_ai_provider', p.id)}
                aria-pressed={isSelected}
                className={cn(
                  'text-left p-4 rounded-xl border transition-all',
                  isSelected
                    ? 'border-primary bg-primary/[0.03] ring-2 ring-primary/40 shadow-sm'
                    : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={cn('w-4 h-4', isSelected ? 'text-primary' : 'text-surface-500')} />
                  <h3 className="font-semibold text-surface-900">{p.label}</h3>
                  {isSelected && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                      <Check size={10} /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-surface-500">{p.blurb}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Section 2: Provider settings, side-by-side ──────────── */}
      {/* Responsive grid: stacks on narrow screens, side-by-side on
          lg and up. Each card dims when not the active provider so
          the eye lands on the active one but the inactive one is
          still editable (super-admin might be prepping a switch). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* — OpenAI — */}
        <section className={cn(
          'bg-surface-0 border rounded-2xl p-6 shadow-sm transition-all',
          provider === 'openai'
            ? 'border-primary/30 ring-1 ring-primary/20'
            : 'border-surface-200 opacity-80',
        )}>
          <SectionHeader
            icon={Zap}
            title="OpenAI"
            subtitle={provider === 'openai' ? 'Active.' : 'Editable but not active — switch above to use.'}
          />

          <div className="mt-4 space-y-4">
            <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === openaiModel)?.hint}>
              <select
                value={openaiModel}
                onChange={(e) => patch('itinerary_openai_model', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Temperature" hint="Lower = deterministic, less flourish. Higher = more variety, more flowery." icon={Thermometer}>
              <SliderRow
                value={openaiTemp}
                min={0} max={2} step={0.1}
                onChange={(v) => patch('itinerary_temperature_openai', v)}
              />
            </Field>
          </div>
        </section>

        {/* — Anthropic — */}
        <section className={cn(
          'bg-surface-0 border rounded-2xl p-6 shadow-sm transition-all',
          provider === 'anthropic'
            ? 'border-primary/30 ring-1 ring-primary/20'
            : 'border-surface-200 opacity-80',
        )}>
          <SectionHeader
            icon={Sparkles}
            title="Anthropic Claude"
            subtitle={provider === 'anthropic' ? 'Active.' : 'Editable but not active — switch above to use.'}
          />

          <div className="mt-4 space-y-4">
            <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === anthropicModel)?.hint}>
              <select
                value={anthropicModel}
                onChange={(e) => patch('itinerary_anthropic_model', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Temperature" hint="Lower = deterministic. Higher = more variety." icon={Thermometer}>
              <SliderRow
                value={anthropicTemp}
                min={0} max={2} step={0.1}
                onChange={(v) => patch('itinerary_temperature_anthropic', v)}
              />
            </Field>

            <Field label="Max output tokens" hint="Hard cap on Claude's response size. A 21-day trip can hit ~30K. Default 32K. Range 4K–64K.">
              <SliderRow
                value={anthropicMaxTok}
                min={4000} max={64000} step={1000}
                format={(v) => `${(v / 1000).toFixed(0)}K`}
                onChange={(v) => patch('itinerary_anthropic_max_tokens', v)}
              />
            </Field>
          </div>
        </section>
      </div>

      {/* ── Section 3: Generation strategy ──────────────────────── */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader icon={Layers} title="Generation strategy" subtitle="How the edge function splits work across LLM calls. Applies to both providers." />

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Parallel mode" hint={ITINERARY_PARALLEL_MODE_OPTIONS.find(m => m.value === parallelMode)?.hint}>
            <select
              value={parallelMode}
              onChange={(e) => patch('itinerary_parallel_mode', e.target.value as ItineraryParallelMode)}
              className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {ITINERARY_PARALLEL_MODE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Brevity mode" hint="Appends length-limit instructions to the prompt — trip summary, cultural notes, food recs, and activity descriptions all get tightened. Useful for verbose Claude models." icon={Wand2}>
            <button
              type="button"
              onClick={() => patch('itinerary_brevity_mode', !brevityMode)}
              aria-pressed={brevityMode}
              className={cn(
                'relative w-12 h-7 rounded-full transition-colors',
                brevityMode ? 'bg-primary' : 'bg-surface-200',
              )}
            >
              <span
                className={cn(
                  'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-surface-0 shadow transition-transform',
                  brevityMode && 'translate-x-5',
                )}
              />
            </button>
            <span className="ml-2 text-xs text-surface-500">{brevityMode ? 'On — outputs shortened' : 'Off — default verbosity'}</span>
          </Field>
        </div>
      </section>

      {/* ── Section 4: Activity enrichment ────────────────────────
          On-demand AI insights fired when a user expands an activity
          card. Short-form (insider tips, photo spots, etc.) so
          benefits from a fast / cheap model — independent from
          itinerary so super-admin can run e.g. OpenAI gpt-4o-mini
          here while keeping Anthropic Sonnet 4-5 for itinerary. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={Sparkles}
          title="Activity enrichment"
          subtitle="On-demand insights when a user expands an activity card. Short-form (~600 tokens) — favours fast models for low click-to-answer latency."
        />

        {/* Provider toggle — identical pattern to itinerary section. */}
        <div className="mt-4 flex items-center gap-2">
          {(['openai', 'anthropic'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => patch('enrichment_ai_provider', p)}
              className={cn(
                'flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all',
                enrichProvider === p
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-surface-0 text-surface-700 border-surface-200 hover:border-primary/40',
              )}
            >
              {p === 'openai' ? 'OpenAI' : 'Anthropic Claude'}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* — OpenAI for enrichment — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            enrichProvider === 'openai'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">OpenAI</span>
              <span className="text-xs text-surface-500 ml-auto">
                {enrichProvider === 'openai' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === enrichOpenaiModel)?.hint ?? 'Defaults to gpt-4o-mini for ~2-3s latency.'}>
                <select
                  value={enrichOpenaiModel}
                  onChange={(e) => patch('enrichment_openai_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Lower keeps tips factual. Default 0.7." icon={Thermometer}>
                <SliderRow
                  value={enrichOpenaiTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('enrichment_temperature_openai', v)}
                />
              </Field>
            </div>
          </section>

          {/* — Anthropic for enrichment — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            enrichProvider === 'anthropic'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">Anthropic Claude</span>
              <span className="text-xs text-surface-500 ml-auto">
                {enrichProvider === 'anthropic' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === enrichAnthropicModel)?.hint}>
                <select
                  value={enrichAnthropicModel}
                  onChange={(e) => patch('enrichment_anthropic_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.7 for grounded tips." icon={Thermometer}>
                <SliderRow
                  value={enrichAnthropicTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('enrichment_temperature_anthropic', v)}
                />
              </Field>
            </div>
          </section>
        </div>
      </section>

      {/* ── Section 5: Blitz refine ────────────────────────────────
          The conversational refine flow on Results (trip-chat edge
          function). Edits one day at a time so it doesn't need 30K
          token outputs — favours fast models for snappy chat-style
          interactions. Independent from itinerary so super-admin
          can run e.g. gpt-4o-mini for Blitz while keeping sonnet-4-5
          for full itinerary generation. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={MessageCircle}
          title="Blitz refine"
          subtitle="Conversational refine on Results. Edits one day at a time — favours fast models for snappy chat-style interactions."
        />

        {/* Provider toggle */}
        <div className="mt-4 flex items-center gap-2">
          {(['openai', 'anthropic'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => patch('blitz_ai_provider', p)}
              className={cn(
                'flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all',
                blitzProvider === p
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-surface-0 text-surface-700 border-surface-200 hover:border-primary/40',
              )}
            >
              {p === 'openai' ? 'OpenAI' : 'Anthropic Claude'}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* — OpenAI for Blitz — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            blitzProvider === 'openai'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">OpenAI</span>
              <span className="text-xs text-surface-500 ml-auto">
                {blitzProvider === 'openai' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === blitzOpenaiModel)?.hint ?? 'Defaults to gpt-4o-mini for fast edits.'}>
                <select
                  value={blitzOpenaiModel}
                  onChange={(e) => patch('blitz_openai_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Lower keeps edits predictable. Default 0.7." icon={Thermometer}>
                <SliderRow
                  value={blitzOpenaiTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('blitz_temperature_openai', v)}
                />
              </Field>
            </div>
          </section>

          {/* — Anthropic for Blitz — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            blitzProvider === 'anthropic'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">Anthropic Claude</span>
              <span className="text-xs text-surface-500 ml-auto">
                {blitzProvider === 'anthropic' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === blitzAnthropicModel)?.hint}>
                <select
                  value={blitzAnthropicModel}
                  onChange={(e) => patch('blitz_anthropic_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.7 for grounded edits." icon={Thermometer}>
                <SliderRow
                  value={blitzAnthropicTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('blitz_temperature_anthropic', v)}
                />
              </Field>
            </div>
          </section>
        </div>
      </section>

      {/* ── Section 6: Insider tab ────────────────────────────────
          generate-insider edge function — cultural etiquette, food
          recommendations, neighborhoods, local tips, seasonal notes.
          Lazy-fetched on Insider tab open. Smaller envelope than
          itinerary so we can target <30s wall clock. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={Globe2}
          title="Insider content"
          subtitle="Cultural etiquette, food picks, neighborhoods — lazy-fetched on the Insider tab. Target sub-30s wall clock."
        />

        {/* Provider toggle */}
        <div className="mt-4 flex items-center gap-2">
          {(['openai', 'anthropic'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => patch('insider_ai_provider', p)}
              className={cn(
                'flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all',
                insiderProvider === p
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-surface-0 text-surface-700 border-surface-200 hover:border-primary/40',
              )}
            >
              {p === 'openai' ? 'OpenAI' : 'Anthropic Claude'}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* — OpenAI for Insider — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            insiderProvider === 'openai'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">OpenAI</span>
              <span className="text-xs text-surface-500 ml-auto">
                {insiderProvider === 'openai' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === insiderOpenaiModel)?.hint ?? 'Default gpt-4o for balanced speed/quality.'}>
                <select
                  value={insiderOpenaiModel}
                  onChange={(e) => patch('insider_openai_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.8 — creative enough for cultural picks." icon={Thermometer}>
                <SliderRow
                  value={insiderOpenaiTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('insider_temperature_openai', v)}
                />
              </Field>
            </div>
          </section>

          {/* — Anthropic for Insider — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            insiderProvider === 'anthropic'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">Anthropic Claude</span>
              <span className="text-xs text-surface-500 ml-auto">
                {insiderProvider === 'anthropic' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === insiderAnthropicModel)?.hint}>
                <select
                  value={insiderAnthropicModel}
                  onChange={(e) => patch('insider_anthropic_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.8." icon={Thermometer}>
                <SliderRow
                  value={insiderAnthropicTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('insider_temperature_anthropic', v)}
                />
              </Field>
              <Field label="Max output tokens" hint="Default 2000 — sized for <30s on Sonnet 4.5. Bump higher if items truncate; lower for cheaper/faster calls.">
                <input
                  type="number"
                  min={500} max={64000} step={500}
                  value={insiderAnthropicMaxTok}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (isFinite(n) && n >= 500 && n <= 64000) patch('insider_anthropic_max_tokens', n);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </Field>
            </div>
          </section>
        </div>
      </section>

      {/* ── Section 7: Requirements tab ───────────────────────────
          generate-trip-requirements edge function — Entry (visa,
          e-auth), Health (vaccines, meds, insurance), Practical
          (IDP, customs, drone rules). Factual content — lower
          temperature default so the LLM leans on regulations rather
          than creativity. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={FileText}
          title="Requirements checklist"
          subtitle="Pre-trip Entry / Health / Practical. Factual content with low temperature; checklist output is small (~4K tokens)."
        />

        {/* Provider toggle */}
        <div className="mt-4 flex items-center gap-2">
          {(['openai', 'anthropic'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => patch('requirements_ai_provider', p)}
              className={cn(
                'flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all',
                reqProvider === p
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-surface-0 text-surface-700 border-surface-200 hover:border-primary/40',
              )}
            >
              {p === 'openai' ? 'OpenAI' : 'Anthropic Claude'}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* — OpenAI for Requirements — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            reqProvider === 'openai'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">OpenAI</span>
              <span className="text-xs text-surface-500 ml-auto">
                {reqProvider === 'openai' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === reqOpenaiModel)?.hint ?? 'Default gpt-4o for accurate compliance content.'}>
                <select
                  value={reqOpenaiModel}
                  onChange={(e) => patch('requirements_openai_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.5 — factual content needs less creativity." icon={Thermometer}>
                <SliderRow
                  value={reqOpenaiTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('requirements_temperature_openai', v)}
                />
              </Field>
            </div>
          </section>

          {/* — Anthropic for Requirements — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            reqProvider === 'anthropic'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">Anthropic Claude</span>
              <span className="text-xs text-surface-500 ml-auto">
                {reqProvider === 'anthropic' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === reqAnthropicModel)?.hint}>
                <select
                  value={reqAnthropicModel}
                  onChange={(e) => patch('requirements_anthropic_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.5." icon={Thermometer}>
                <SliderRow
                  value={reqAnthropicTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('requirements_temperature_anthropic', v)}
                />
              </Field>
              <Field label="Max output tokens" hint="Default 2000 — checklist is small. Range 500-64000.">
                <input
                  type="number"
                  min={500} max={64000} step={500}
                  value={reqAnthropicMaxTok}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (isFinite(n) && n >= 500 && n <= 64000) patch('requirements_anthropic_max_tokens', n);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </Field>
            </div>
          </section>
        </div>
      </section>

      {/* ── Section 8: Journal Companion ──────────────────────────
          generate-daily-journal + generate-trip-journal edge functions.
          Narrative prose drafting from notes, photos, and itinerary
          context. Default provider is Anthropic because Claude's tone
          control suits storytelling; max_tokens default is higher
          (4000) than Blitz/Enrichment because trip-level recaps run
          4-8 paragraphs plus per-day highlight blocks. The edge fns
          fall back to itinerary_* when journal_* are null, so
          existing deployments keep their current behaviour until the
          operator picks values here. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={BookOpen}
          title="Journal Companion"
          subtitle="Narrative prose for daily entries + trip recaps. Drafts only — never publishes. Falls back to Itinerary settings when not configured."
        />

        {/* Provider toggle */}
        <div className="mt-4 flex items-center gap-2">
          {(['openai', 'anthropic'] as const).map(p => (
            <button
              key={p}
              type="button"
              onClick={() => patch('journal_ai_provider', p)}
              className={cn(
                'flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all',
                journalProvider === p
                  ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                  : 'bg-surface-0 text-surface-700 border-surface-200 hover:border-primary/40',
              )}
            >
              {p === 'openai' ? 'OpenAI' : 'Anthropic Claude'}
            </button>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* — OpenAI for Journal — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            journalProvider === 'openai'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">OpenAI</span>
              <span className="text-xs text-surface-500 ml-auto">
                {journalProvider === 'openai' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_OPENAI_MODEL_OPTIONS.find(m => m.value === journalOpenaiModel)?.hint ?? 'Default gpt-4o-mini — good narrative on a small budget.'}>
                <select
                  value={journalOpenaiModel}
                  onChange={(e) => patch('journal_openai_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_OPENAI_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="0.6–0.8 gives warmth without inventing facts. Default 0.7." icon={Thermometer}>
                <SliderRow
                  value={journalOpenaiTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('journal_temperature_openai', v)}
                />
              </Field>
            </div>
          </section>

          {/* — Anthropic for Journal — */}
          <section className={cn(
            'bg-surface-50 border rounded-2xl p-5 transition-all',
            journalProvider === 'anthropic'
              ? 'border-primary/30 ring-1 ring-primary/20'
              : 'border-surface-200 opacity-80',
          )}>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm text-surface-900">Anthropic Claude</span>
              <span className="text-xs text-surface-500 ml-auto">
                {journalProvider === 'anthropic' ? 'Active' : 'Not active'}
              </span>
            </div>
            <div className="space-y-4">
              <Field label="Model" hint={ITINERARY_ANTHROPIC_MODEL_OPTIONS.find(m => m.value === journalAnthropicModel)?.hint}>
                <select
                  value={journalAnthropicModel}
                  onChange={(e) => patch('journal_anthropic_model', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {ITINERARY_ANTHROPIC_MODEL_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </Field>
              <Field label="Temperature" hint="Default 0.7 — natural prose, light variability." icon={Thermometer}>
                <SliderRow
                  value={journalAnthropicTemp}
                  min={0} max={2} step={0.1}
                  onChange={(v) => patch('journal_temperature_anthropic', v)}
                />
              </Field>
              <Field label="Max output tokens" hint="Trip recaps + per-day highlights need headroom. Default 4000. Range 500-16000.">
                <input
                  type="number"
                  min={500} max={16000} step={500}
                  value={journalAnthropicMaxTok}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (isFinite(n) && n >= 500 && n <= 16000) patch('journal_anthropic_max_tokens', n);
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </Field>
            </div>
          </section>
        </div>
      </section>

      {/* ── Trip Poster image provider ─────────────────────────────
          IMAGE generation, not LLM. Single binary toggle — OpenAI's
          gpt-image-1 (current default) vs Google's Imagen 4. No model
          / temp / max_tokens picker per provider because the image
          fleet doesn't have meaningful knobs at that level today; we
          pin a recommended model on each side server-side. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader icon={Cpu} title="Trip Poster image provider" subtitle="Which image-gen API the generate-trip-poster edge function calls." />

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          {([
            {
              id:    'openai' as PosterImageProvider,
              label: 'OpenAI · gpt-image-1',
              blurb: 'Default. Decent at composition + photorealism. Costs ~$0.19/image at HD. Needs OPENAI_API_KEY (already set).',
            },
            {
              id:    'google' as PosterImageProvider,
              label: 'Google · Imagen 4',
              blurb: 'Sharper text rendering for the infographic templates (labels, travel times). ~$0.04/image. Needs GOOGLE_AI_API_KEY in Supabase Secrets.',
            },
            {
              id:    'fal' as PosterImageProvider,
              label: 'fal.ai · Ideogram V3',
              blurb: 'Best at rendering poster titles legibly in-image ("10 Days on the Alpine Riviera"). ~$0.06/image. Needs FAL_API_KEY in Supabase Secrets.',
            },
          ]).map(p => {
            const isSelected = posterImageProvider === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => patch('poster_image_provider', p.id)}
                aria-pressed={isSelected}
                className={cn(
                  'text-left p-4 rounded-xl border transition-all',
                  isSelected
                    ? 'border-primary bg-primary/[0.03] ring-2 ring-primary/40 shadow-sm'
                    : 'border-surface-200 hover:border-surface-300 hover:bg-surface-50',
                )}
              >
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-surface-900">{p.label}</h3>
                  {isSelected && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                      <Check size={10} /> Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-surface-500">{p.blurb}</p>
              </button>
            );
          })}
        </div>

        {/* Per-provider model string. Only the active provider's input
            shows so the page stays uncluttered. Free-text — paste
            anything OpenAI/Google/fal will accept (e.g. 'gpt-image-2',
            'fal-ai/flux-pro/v1.1-ultra'). Empty = the edge fn falls
            back to its hardcoded default. */}
        <div className="mt-5 pt-5 border-t border-surface-200">
          {(() => {
            const cfg = posterImageProvider === 'google'
              ? { label: 'Google Imagen model',  value: posterGoogleModel, field: 'poster_google_model' as const, placeholder: 'imagen-4.0-generate-preview-06-06', hint: 'Anything api.openai.com → Google Imagen ships; default rev is the 4.0 preview.' }
              : posterImageProvider === 'fal'
              ? { label: 'fal.ai model path',    value: posterFalModel,    field: 'poster_fal_model'    as const, placeholder: 'fal-ai/ideogram/v3',                   hint: 'Format: fal-ai/{model}/{version}. Other options: fal-ai/flux-pro/v1.1-ultra, fal-ai/recraft-v3.' }
              : { label: 'OpenAI image model',   value: posterOpenaiModel, field: 'poster_openai_model' as const, placeholder: 'gpt-image-1',                          hint: 'Type gpt-image-2 (or future versions) the moment OpenAI ships them — no redeploy needed.' };
            return (
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1.5">{cfg.label}</label>
                <input
                  type="text"
                  value={cfg.value}
                  onChange={(e) => patch(cfg.field, e.target.value)}
                  placeholder={cfg.placeholder}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-surface-200 bg-surface-0 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
                <p className="text-xs text-surface-500 mt-1.5">{cfg.hint}</p>
              </div>
            );
          })()}
        </div>
      </section>

      {/* ── Trip Poster Marketplace ──────────────────────────────────
          Paid-clone economics. Three controls on one card:
            • Kill switch (poster_paid_clones_enabled)
            • Price tier ladder editable as a comma-separated cents
              list. Phase-1 keeps it simple — power-users only — but
              the list is the SAME data the publish modal renders, so
              changes go live within the App's 60s settings cache.
            • Author revenue share — 0.0 to 1.0 slider with a $5
              preview to translate the abstract fraction into dollars
              the super-admin can reason about. */}
      <section className="bg-surface-0 border border-surface-200 rounded-2xl p-6 shadow-sm">
        <SectionHeader
          icon={Sparkles}
          title="Trip Poster Marketplace"
          subtitle="Paid-clone kill switch, price tiers, and author revenue share. App reads via 60s cache."
        />

        <div className="mt-4 space-y-5">
          {/* Kill switch */}
          <Field label="Paid clones enabled" hint="When off, the publish modal hides the price selector and any existing paid poster falls back to Free at the gate.">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => patch('poster_paid_clones_enabled', !posterPaidEnabled)}
                aria-pressed={posterPaidEnabled}
                className={cn(
                  'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                  posterPaidEnabled ? 'bg-primary' : 'bg-surface-300',
                )}
              >
                <span className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                  posterPaidEnabled ? 'translate-x-6' : 'translate-x-1',
                )} />
              </button>
              <span className="text-sm text-surface-700">
                {posterPaidEnabled
                  ? <>Paid clones <strong>active</strong>. Authors can charge $3–$10 (or whatever tiers you set below).</>
                  : <>Paid clones <strong>disabled</strong> platform-wide. Posters default to Free.</>}
              </span>
            </div>
          </Field>

          {/* Price tier ladder — editable as comma-separated cents.
              Power-user input by design; the publish modal renders
              whatever values appear here. Validates client-side that
              0 is included as the first entry. */}
          <Field
            label="Price tier ladder (cents, comma-separated)"
            hint="The author picks one of these at publish-time. Must start with 0 (Free). Example: 0, 300, 500, 1000 = Free / $3 / $5 / $10."
          >
            <input
              type="text"
              value={posterPriceTiers.join(', ')}
              onChange={(e) => {
                // Parse the input as a list of non-negative integers.
                // Empty entries are dropped; non-numeric falls back to
                // the existing tiers so a half-typed value doesn't
                // wipe the field. The DB has its own CHECK that the
                // value is a jsonb array.
                const parsed = e.target.value
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
                  .map(s => Math.max(0, Math.floor(Number(s))))
                  .filter(n => Number.isFinite(n));
                patch('poster_price_tiers_cents', parsed.length > 0 ? parsed : [0]);
              }}
              placeholder="0, 300, 500, 1000"
              className="w-full px-3 py-2 text-sm font-mono rounded-lg border border-surface-200 bg-surface-0 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary"
              spellCheck={false}
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {posterPriceTiers.map((c, i) => (
                <span
                  key={i}
                  className={cn(
                    'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border',
                    c === 0
                      ? 'bg-surface-100 text-surface-600 border-surface-300'
                      : 'bg-primary/10 text-primary border-primary/30',
                  )}
                >
                  {c === 0 ? 'Free' : `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`}
                </span>
              ))}
              {!posterPriceTiers.includes(0) && (
                <span className="inline-flex items-center gap-1 text-[11px] text-red-600">
                  <AlertTriangle className="w-3 h-3" />
                  Missing 0 (Free) — authors must always be allowed to publish free.
                </span>
              )}
            </div>
          </Field>

          {/* Author revenue share — slider with a $5 preview to
              translate fraction → dollars. */}
          <Field
            label="Author revenue share"
            hint="What fraction of each paid-clone payment goes to the author (snapshotted on poster_clone_payments.author_share_cents at PI-create time). Remainder stays with Platform."
            icon={Sparkles}
          >
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={posterAuthorShare}
                onChange={(e) => patch('poster_author_revenue_share', Number(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-mono font-semibold text-surface-900 w-16 text-right">
                {(posterAuthorShare * 100).toFixed(0)}%
              </span>
            </div>
            <p className="text-xs text-surface-500 mt-2">
              At $5: author gets <strong>${(5 * posterAuthorShare).toFixed(2)}</strong>,
              Platform gets <strong>${(5 * (1 - posterAuthorShare)).toFixed(2)}</strong> (before Stripe fees).
              Defaults to 80% — creator-marketplace standard.
            </p>
          </Field>

          {/* Custom-price input bounds — the "Custom" chip on
              PosterGeneratePage opens a numeric input clamped to
              these. UX-only: server doesn't enforce on publish, the
              only DB check is price_tier_cents >= 0. Hard absolute
              ceiling lives in the DB CHECK constraint at $999.99
              (99999 cents). */}
          <Field
            label="Custom-price bounds"
            hint="Min/max the Custom chip on PosterGeneratePage accepts. UX guardrail only — DB CHECK enforces an absolute $999.99 ceiling regardless. Values in cents."
            icon={Sparkles}
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-surface-600 mb-1">Min (cents)</label>
                <input
                  type="number"
                  min={1}
                  max={posterMaxCustom - 1}
                  step={1}
                  value={posterMinCustom}
                  onChange={(e) => patch('poster_min_custom_cents', Math.max(1, Math.floor(Number(e.target.value) || 0)))}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <p className="text-[10px] text-surface-500 mt-1">= ${(posterMinCustom / 100).toFixed(2)}</p>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-surface-600 mb-1">Max (cents)</label>
                <input
                  type="number"
                  min={posterMinCustom + 1}
                  max={99999}
                  step={1}
                  value={posterMaxCustom}
                  onChange={(e) => patch('poster_max_custom_cents', Math.min(99999, Math.floor(Number(e.target.value) || 0)))}
                  className="w-full px-3 py-2 rounded-lg border border-surface-200 bg-surface-0 text-sm font-mono text-surface-900 focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <p className="text-[10px] text-surface-500 mt-1">= ${(posterMaxCustom / 100).toFixed(2)}</p>
              </div>
            </div>
            {posterMaxCustom <= posterMinCustom && (
              <p className="flex items-center gap-1 text-[11px] text-red-600 mt-2">
                <AlertTriangle className="w-3 h-3" />
                Max must be greater than min.
              </p>
            )}
          </Field>
        </div>
      </section>

      {/* Pricing matrix moved to a dedicated nav entry:
          /admin/infrastructure-pricing. Kept the component file in
          place so its history + the cross-import from the new page
          stay valid. */}

      {functionsModalOpen && (
        <FunctionsModal onClose={() => setFunctionsModalOpen(false)} />
      )}

      {/* Save row */}
      <div className="flex items-center justify-end gap-3 pt-2 sticky bottom-4">
        <div className="flex-1">
          {error && (
            <div className="flex items-center gap-1.5 text-sm text-red-600">
              <AlertTriangle size={14} />
              <span>{error}</span>
            </div>
          )}
          {dirty && !error && (
            <div className="text-xs text-surface-500">
              {dirtyFields.length} change{dirtyFields.length === 1 ? '' : 's'} pending
            </div>
          )}
        </div>
        {saved && (
          <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
            <Check size={14} /> Saved
          </span>
        )}
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className={cn(
            'inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-sm',
            dirty && !saving
              ? 'bg-primary text-white hover:bg-primary/90'
              : 'bg-surface-100 text-surface-400 cursor-not-allowed',
          )}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

// ─── Small UI primitives ────────────────────────────────────────────────

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ComponentType<{ className?: string }>; title: string; subtitle: string }) {
  return (
    <header className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-surface-900">{title}</h2>
        <p className="text-sm text-surface-500 mt-0.5">{subtitle}</p>
      </div>
    </header>
  );
}

function Field({ label, hint, icon: Icon, children }: { label: string; hint?: string; icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-surface-500" />}
        <label className="text-xs font-semibold text-surface-700 uppercase tracking-wide">{label}</label>
      </div>
      <div className="flex items-center flex-wrap gap-2">
        {children}
      </div>
      {hint && (
        <p className="text-xs text-surface-500 mt-1.5 leading-snug">{hint}</p>
      )}
    </div>
  );
}

function SliderRow({ value, min, max, step, format, onChange }: { value: number; min: number; max: number; step: number; format?: (v: number) => string; onChange: (v: number) => void }) {
  return (
    <>
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 accent-primary"
      />
      <span className="font-mono text-sm tabular-nums text-surface-900 min-w-[3.5rem] text-right">
        {format ? format(value) : value.toFixed(step < 1 ? 1 : 0)}
      </span>
    </>
  );
}

export function SecretRow({ name, desc }: { name: string; desc: string }) {
  return (
    <tr className="align-top">
      <td className="py-1.5 pr-3 whitespace-nowrap">
        <code className="text-[11px] bg-surface-100 text-surface-900 px-1.5 py-0.5 rounded font-mono">{name}</code>
      </td>
      <td className="py-1.5 text-[12px] text-surface-800 leading-snug">{desc}</td>
    </tr>
  );
}


// Manageable secret row — live Set/Unset status + Unset button.
// Renders one row in the Optional overrides section for each of the
// three secrets that we can delete via the Supabase Management API.
export function ManageableSecretRow({
  name,
  desc,
  isSet,
  unsetAt,
  unsetByUser,
  busy,
  onUnset,
  onRefresh,
  refreshing,
}: {
  name:         string;
  desc:         string;
  isSet:        boolean;
  /** ISO timestamp of when this secret was last cleared via the UI. */
  unsetAt:      string | null;
  /** auth.users.id of the super-admin who cleared it. We display the
   *  raw uuid; a future enhancement could resolve to a display name. */
  unsetByUser:  string | null;
  busy:         boolean;
  onUnset:      () => void;
  /** Per-row refresh — re-queries Supabase Management API for this
   *  one secret's status. The section-header Refresh re-queries all
   *  three; this exists so the user can verify a single secret
   *  without affecting the others' loading indicators. */
  onRefresh:    () => void;
  refreshing:   boolean;
}) {
  return (
    <div className="rounded-lg border border-amber-200/60 bg-surface-0 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-[11px] font-mono bg-surface-100 text-surface-900 px-1.5 py-0.5 rounded">
              {name}
            </code>
            {isSet ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                <ShieldAlert size={10} /> Set — overriding UI
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-200">
                <Check size={10} /> Unset — UI active
              </span>
            )}
          </div>
          <p className="text-[11px] text-surface-600 mt-1 leading-snug">{desc}</p>
          {!isSet && unsetAt && (
            <p className="text-[10px] text-surface-500 mt-1 italic">
              Cleared {formatRelative(unsetAt)}{unsetByUser ? ` by ${unsetByUser.slice(0, 8)}…` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Per-row refresh button — re-checks status for all
              three secrets (the API doesn't support single-secret
              read so this just kicks the shared load). Always
              visible regardless of set/unset so users can verify
              after manually editing in the Supabase dashboard. */}
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-amber-200 bg-surface-0 hover:bg-amber-50 text-amber-700 transition-colors disabled:opacity-60"
            title={`Re-check ${name} status`}
            aria-label={`Refresh ${name} status`}
          >
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
          </button>
          {isSet && (
            <button
              type="button"
              onClick={onUnset}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-[11px] font-semibold transition-colors disabled:opacity-60"
              title={`Delete ${name} from App edge-function secrets`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
              {busy ? 'Unsetting…' : 'Unset'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── AI Function Usage Reference ─────────────────────────────────────
// Hardcoded inventory of every AI-using edge function in the App
// project, grouped by trip surface. Provider + model + description
// must stay in sync with each function's source. When a new AI-using
// function ships (or an existing one switches provider/model),
// update this list.
//
// Provider tags are normalised:
//   • 'openai'     — always OpenAI
//   • 'anthropic'  — always Anthropic
//   • 'either'     — controllable via this AI Settings page (currently
//                    only generate-itinerary)

type FnInfo = {
  name:        string;
  provider:    'openai' | 'anthropic' | 'either';
  model:       string;
  description: string;
};

type FnGroup = {
  title:    string;
  subtitle: string;
  items:    FnInfo[];
};

const FUNCTION_INVENTORY: FnGroup[] = [
  {
    title: 'Trip Builder',
    subtitle: 'Functions that run during trip planning — before the trip starts.',
    items: [
      {
        name:        'clarify-trip-plan',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Each turn of the AI Trip Builder chat. Parses user intent and asks clarifying questions for missing details (destination, dates, origin, vibe).',
      },
      {
        name:        'duration-check',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Trip sanity check before generation. Returns verdict (tight / comfortable / generous) + recommended day count + jet-lag buffer.',
      },
      {
        name:        'check-budget-fit',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Validates the trip budget against destinations + group size during the Builder\'s companions step. Surfaces "tight" warnings before generation.',
      },
      {
        name:        'parse-ai-trip',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Quick 5-question flow parser. Extracts structured trip data (destinations, days, interests, mood) from conversational answers.',
      },
      {
        name:        'generate-itinerary',
        provider:    'either',
        model:       'gpt-4o / claude-sonnet-4-5 (configurable above)',
        description: 'The big one. Generates the full multi-day itinerary with day-by-day activities, hotel picks, cultural notes, visa info. Configurable provider + parallel mode + brevity from this page.',
      },
      {
        name:        'trip-chat',
        provider:    'either',
        model:       'gpt-4o-mini / claude-sonnet-4-5 (Blitz refine section above)',
        description: 'Blitz — the conversational refine flow on Results. Tweaks individual days or the whole trip based on a chat message. Configurable INDEPENDENTLY of itinerary via the Blitz refine card on this page.',
      },
      {
        name:        'enrich-activity',
        provider:    'either',
        model:       'gpt-4o-mini / claude-sonnet-4-5 (Activity enrichment section above)',
        description: 'On-demand AI insights when a user expands an activity card on Results — insider tips, best time, what to bring, photo spots, nearby picks. Configurable INDEPENDENTLY of itinerary via the Activity enrichment card on this page.',
      },
      {
        name:        'generate-hotel-picks',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'On-demand hotel recommendations for a city. Fills missing recommended_hotels arrays on the itinerary Stay tab.',
      },
    ],
  },
  {
    title: 'Live Trip',
    subtitle: 'Functions that run while the trip is active — companion + nudges.',
    items: [
      {
        name:        'smart-companion',
        provider:    'anthropic',
        model:       'claude-sonnet-4-20250514',
        description: 'Context-aware assistant during the active trip. Handles Situation / Price Check / Emergency queries with full trip context.',
      },
      {
        name:        'copilot-nudge',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Background nudges throughout the day (morning briefing, skip suggestions, budget alerts, weather updates, end-of-day reflection).',
      },
      {
        name:        'local-insights',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Per-activity enrichment. Secret tips, etiquette pointers, and local phrases on each activity card during the active trip.',
      },
      {
        name:        'flight-guide',
        provider:    'openai',
        model:       'gpt-4o',
        description: 'Generates arrival or delay guides per flight (lounges, food, currency, ground transport, customs). Trip Guardian phase 2.1.',
      },
      {
        name:        'visual-context',
        provider:    'anthropic',
        model:       'claude-sonnet-4-20250514',
        description: 'Photo recognition while travelling. Identifies landmarks, food, signs, art. Provides cultural context + local pricing fairness.',
      },
      {
        name:        'cultural-guide',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Pre-travel + in-trip cultural Q&A. Answers about customs, greetings, dress codes, dining etiquette, taboos.',
      },
    ],
  },
  {
    title: 'Group & Buddies',
    subtitle: 'Functions used in Private Trip Chats and Buddy Trips.',
    items: [
      {
        name:        'summarize-itinerary-preferences',
        provider:    'anthropic',
        model:       'claude-sonnet-4-20250514',
        description: 'Private-chat Summary modal. Surfaces the group\'s collective vote rankings + chat highlights of emerging decisions.',
      },
      {
        name:        'extract-friend-insights',
        provider:    'openai',
        model:       'gpt-4o',
        description: 'Friend Insights phase 1. Extracts conversation from a WhatsApp/iMessage screenshot, returns speaker-attributed text + strips PII.',
      },
    ],
  },
  {
    title: 'Other tools',
    subtitle: 'Functions that support import flows, packing, and app help.',
    items: [
      {
        name:        'extract-itinerary',
        provider:    'anthropic',
        model:       'claude-sonnet-4-6',
        description: 'Trip Import. Extracts a structured trip from a URL or pasted text into the app\'s itinerary schema.',
      },
      {
        name:        'packing-list',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'Generates a smart packing list per trip. Tailored to destinations, season, climate, duration, and travel companions.',
      },
      {
        name:        'app-assistant',
        provider:    'openai',
        model:       'gpt-4o-mini',
        description: 'In-app help. Answers questions about app features, navigation, and best practices.',
      },
    ],
  },
];

function FunctionsModal({ onClose }: { onClose: () => void }) {
  // Esc-to-close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-4xl bg-surface-0 rounded-2xl shadow-2xl border border-surface-200 my-8"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="AI Function Usage"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-surface-200">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-surface-900">AI Function Usage</h2>
              <p className="text-sm text-surface-600 mt-0.5">
                Which AI provider runs each edge function in this app, and when each one fires.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg text-surface-500 hover:bg-surface-100 hover:text-surface-900 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          <div className="rounded-lg bg-surface-50 border border-surface-200 p-3 text-xs text-surface-700 leading-snug">
            <strong className="text-surface-900">Heads up:</strong> three functions are configurable from this page — <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">generate-itinerary</code> + <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">trip-chat</code> share the itinerary settings; <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">enrich-activity</code> has its own Activity enrichment card. The rest have their provider/model hardcoded in the function source — to change them you need a code commit.
          </div>

          {FUNCTION_INVENTORY.map((group) => (
            <div key={group.title} className="border-l-4 border-primary/40 pl-4">
              <h3 className="text-base font-bold text-surface-900">{group.title}</h3>
              <p className="text-sm text-surface-600 mt-0.5 mb-3 leading-snug">{group.subtitle}</p>
              <div className="space-y-2">
                {group.items.map((fn) => (
                  <FunctionRow key={fn.name} info={fn} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-surface-200 flex items-center justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FunctionRow({ info }: { info: FnInfo }) {
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-0 p-3">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
        <code className="text-xs font-mono bg-surface-100 text-surface-900 px-1.5 py-0.5 rounded">
          {info.name}
        </code>
        <ProviderBadge provider={info.provider} model={info.model} />
      </div>
      <p className="text-sm text-surface-800 leading-snug">{info.description}</p>
    </div>
  );
}

function ProviderBadge({ provider, model }: { provider: FnInfo['provider']; model: string }) {
  // Provider-specific tint so the eye can scan the whole table by
  // colour ("how many are OpenAI?" / "where do we use Anthropic?").
  // Either = configurable, distinct treatment (primary blue) so it
  // stands out as "this one's special — controlled by this page".
  let cls = 'bg-emerald-100 text-emerald-800 border-emerald-200';
  let label = 'OpenAI';
  if (provider === 'anthropic') {
    cls = 'bg-violet-100 text-violet-800 border-violet-200';
    label = 'Anthropic';
  } else if (provider === 'either') {
    cls = 'bg-primary/10 text-primary border-primary/30';
    label = 'Configurable';
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5 shrink-0">
      <span className={cn('inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border', cls)}>
        {label}
      </span>
      <code className="text-[10px] font-mono text-surface-500 max-w-[200px] truncate">
        {model}
      </code>
    </span>
  );
}

// Human-readable relative timestamp ("3m ago", "5h ago", "yesterday",
// "2d ago", or a date for older). Kept inline because we only need it
// here and we don't want to pull in dayjs/date-fns for one usage.
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const sec  = Math.round(diffMs / 1000);
  const min  = Math.round(diffMs / 60_000);
  const hr   = Math.round(diffMs / 3_600_000);
  const day  = Math.round(diffMs / 86_400_000);
  if (sec  < 30)        return 'just now';
  if (min  < 1)         return `${sec}s ago`;
  if (min  < 60)        return `${min}m ago`;
  if (hr   < 24)        return `${hr}h ago`;
  if (day  === 1)       return 'yesterday';
  if (day  < 7)         return `${day}d ago`;
  // Older — show absolute date so the super-admin sees the actual
  // calendar context.
  return new Date(iso).toLocaleDateString();
}
